import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppError, useAuth } from '@/contexts/AuthContext';
import { AuthShell } from '@/components/auth/AuthShell';
import { AuthHeading, MonoLabel, PillButton, PillLink } from '@/components/editorial';
import { asSentence } from '@/lib/as-sentence';
import { displayNameOf } from '@/lib/display-name';
import { homeFor } from '@/lib/home-route';

type State = 'verifying' | 'error';

/**
 * The sentence for each failure, chosen by the server's stable `code` and read
 * from the catalog in the reader's language (#2342). The server's own
 * `message` is English and is never shown: placed inside the translated lede
 * it made a zh parent read English then Chinese. A failure with no known code
 * (rate limit, network, a 5xx) reads the generic sentence.
 */
const ERROR_KEY = {
  MAGIC_LINK_INVALID: 'magicLink.invalidOrExpired',
  ACCOUNT_NOT_ACTIVE: 'magicLink.accountNotActive',
} as const;
type ErrorKey = (typeof ERROR_KEY)[keyof typeof ERROR_KEY] | 'magicLink.signInFailed' | 'magicLink.missingToken';

function errorKeyFor(err: unknown): ErrorKey {
  const code = err instanceof AppError ? err.code : undefined;
  return (code && (ERROR_KEY as Record<string, ErrorKey | undefined>)[code]) || 'magicLink.signInFailed';
}

/** Eyebrow, title and lede, shared by the interstitial and the failure states. */
function NoticeHeader({ eyebrow, title, lede }: { eyebrow: string; title: string; lede: string }) {
  return (
    <header className="flex flex-col gap-4 mb-8">
      <MonoLabel>{eyebrow}</MonoLabel>
      <AuthHeading>
        {title}
      </AuthHeading>
      <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
        {lede}
      </p>
    </header>
  );
}

export function MagicLinkPage() {
  const [searchParams] = useSearchParams();
  const { loginWithMagicLink, signOutQuietly, loading, user, sessionCheckFailed } = useAuth();
  const { t } = useTranslation('auth');
  const token = searchParams.get('token');
  const [state, setState] = useState<State>(token ? 'verifying' : 'error');
  const [errorKey, setErrorKey] = useState<ErrorKey>(
    token ? 'magicLink.signInFailed' : 'magicLink.missingToken',
  );
  // #2439 must-fix: a live session is never ended without the person's
  // choice. Set by the interstitial's Continue.
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  // Guard against React strict-mode double-mount triggering two verify calls
  // (would race-condition: first deletes the token, second sees "invalid").
  const startedRef = useRef(false);

  // #2439: wait for the provider's boot session check. On web that check
  // refreshes from the browser's cookie, and if it lands after the verify it
  // re-adopts whoever held the cookie — the previous person.
  //
  // Once it has settled, a session that is still live gets the interstitial
  // below instead of a verify (a forced logout by any link, or a silent
  // switch into someone else's account, were the two holes). Continue
  // (#2439 simplification) runs the ordinary sign-out without navigating,
  // then verifies exactly as a signed-out tab would. A bad link then shows
  // the error state with the person signed out, which they chose.
  useEffect(() => {
    if (!token || loading || startedRef.current) return;
    if (user && !replaceConfirmed) return;
    startedRef.current = true;
    // A boot check that could not answer (network, 5xx) is not "nobody is
    // signed in": verifying would replace a live session without Continue.
    // The try-again state, with the link unspent.
    if (!user && sessionCheckFailed) {
      setErrorKey('magicLink.signInFailed');
      setState('error');
      return;
    }
    const replacing = Boolean(user);
    (async () => {
      try {
        if (replacing) await signOutQuietly();
        await loginWithMagicLink(token, true);
        // On success, loginWithMagicLink navigates to the role's home (or /mfa).
      } catch (err) {
        setErrorKey(errorKeyFor(err));
        setState('error');
      }
    })();
  }, [token, loading, user, sessionCheckFailed, replaceConfirmed, loginWithMagicLink, signOutQuietly]);

  if (state === 'verifying' && token && !loading && user && !replaceConfirmed && !startedRef.current) {
    const name = displayNameOf(user);
    return (
      <AuthShell documentLabel={t('magicLink.replaceSessionDocumentLabel')}>
        <NoticeHeader
          eyebrow={t('magicLink.replaceSessionEyebrow')}
          title={t('magicLink.replaceSessionTitle')}
          lede={t('magicLink.replaceSessionLede', { name })}
        />

        <div className="flex flex-wrap items-center gap-3">
          <PillButton type="button" onClick={() => setReplaceConfirmed(true)}>
            {t('magicLink.replaceSessionContinue')}
          </PillButton>
          {/* Leaves the link unconsumed: no verify, no sign-out. Goes to the
              person's own home (an applicant's is the application), and is a
              44 px target on a coarse pointer. */}
          <Link
            to={homeFor(user.role)}
            replace
            className="inline-flex items-center [@media(pointer:coarse)]:min-h-11 font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
          >
            {t('magicLink.replaceSessionStay', { name })}
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (state === 'verifying') {
    return (
      <AuthShell documentLabel={t('magicLink.verifyingDocumentLabel')}>
        <header className="flex flex-col gap-4 mb-8">
          <MonoLabel>{t('magicLink.oneMoment')}</MonoLabel>
          <AuthHeading>
            {t('magicLink.verifyingTitle')}
          </AuthHeading>
        </header>
        <div className="flex items-center gap-3 text-stevens-ink-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-lede">{t('magicLink.verifyingLink')}</span>
        </div>
      </AuthShell>
    );
  }

  // #2353: only a bad or spent link is fixed by a new one. An inactive
  // account and a failure with no code (rate limit, network, a 5xx) each say
  // what will help instead of "request a new one".
  let eyebrow: string, title: string, lede: string;
  let documentLabel: string = t('magicLink.blockedEyebrow');
  if (errorKey === 'magicLink.accountNotActive') {
    eyebrow = t('magicLink.blockedEyebrow');
    title = t('magicLink.accountNotActive');
    lede = t('magicLink.inactiveLede');
  } else if (errorKey === 'magicLink.signInFailed') {
    eyebrow = t('magicLink.blockedEyebrow');
    title = t('magicLink.failedTitle');
    lede = t('magicLink.failedLede');
  } else {
    documentLabel = t('magicLink.expiredDocumentLabel');
    eyebrow = t('magicLink.expiredEyebrow');
    title = t('magicLink.expiredTitle');
    // The message is a catalog sentence in the reader's language, some with
    // no full stop; the lede writes its own sentence after it.
    lede = t('magicLink.expiredLede', { message: asSentence(t(errorKey)) });
  }

  return (
    <AuthShell documentLabel={documentLabel}>
      <NoticeHeader eyebrow={eyebrow} title={title} lede={lede} />

      <div className="flex items-center gap-3">
        <PillLink href="/login" className="no-underline">
          {t('magicLink.backToSignIn')} {"→"}
        </PillLink>
        <Link
          to="/login"
          className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
        >
          {t('magicLink.usePassword')}
        </Link>
      </div>
    </AuthShell>
  );
}
